import {useCallback, useEffect, useRef, useState} from 'react'
import {getSkus} from '../../../services/api/apiProductionPlanning'

/** Chờ bấy nhiêu ms sau ký tự cuối mới hỏi server — gõ một mã SKU không nên đẻ ra chục request. */
const SEARCH_DELAY_MS = 300

/**
 * Danh mục SKU cho ô chọn, TÌM PHÍA SERVER.
 *
 * ⚠️ Vì sao không thể chỉ nạp một lần rồi lọc tại chỗ: `GET /config/standard-time/skus` phân trang,
 *    và hook này KHÔNG gửi `limit` nên nhận đúng mức mặc định của service — tức ngưỡng mù bắt đầu
 *    ngay sau trang đầu, sớm hơn nhiều so với cap `limits.maxPageSize`. Danh mục thật lớn hơn một
 *    trang. Ô chọn `showSearch` của antd lọc trên ĐÚNG các option đang có, nên SKU nằm ngoài trang
 *    đầu là **gõ đúng mã vẫn không ra** — không lỗi, không cảnh báo, người dùng chỉ thấy "không có
 *    SKU này" rồi đi mở màn Quản lý SKU để đối chiếu.
 *    (Đo 23/09/2026 trên môi trường thật: `LGLASS_ORN_0406MT` vắng mặt ở cả `limit=500`, nhưng
 *    `?q=` tìm ra ngay. Số đo NGOÀI repo — không có fixture nào trong repo chứng thực.)
 *
 * Endpoint ĐÃ có tham số `q` và lọc phía server; chỗ thiếu chỉ là màn hình chưa bao giờ gửi nó.
 *
 * GỘP chứ không thay: kết quả tìm được cộng vào danh sách sẵn có, nên SKU đã chọn không biến mất
 * khỏi ô khi người dùng gõ một từ khoá khác. Client vẫn lọc như cũ trên tập đã gộp, nên phần gõ
 * phím không phải chờ mạng.
 */
export default function useSkuOptions({onFirstLoad, onError, includeInactive = false} = {}) {
    const [skus, setSkus] = useState([])
    const [skuError, setSkuError] = useState(false)
    const [skuSearching, setSkuSearching] = useState(false)
    /** Lượt tìm gần nhất HỎNG — khác hẳn "tìm xong và thật sự không có mã nào khớp". */
    const [skuSearchFailed, setSkuSearchFailed] = useState(false)
    const timer = useRef(null)
    /*
     * Chốt unmount dùng CHUNG cho cả lượt nạp đầu lẫn lượt tìm.
     *
     * Trước đây `alive` là biến cục bộ của effect nên chỉ che được lượt nạp đầu; request của
     * `searchSkus` đã bay đi vẫn `merge`/`setSkuSearching` sau khi component chết — đo được cảnh
     * báo "state update on an unmounted component". `LookupPage` là modal, đóng giữa lúc gõ là
     * chuyện thường, và docblock của chính màn đó gọi chốt này là bắt buộc.
     */
    const alive = useRef(true)
    // Chỉ nhận kết quả của lượt tìm MỚI NHẤT: gõ nhanh thì lượt cũ có thể về sau lượt mới.
    const seq = useRef(0)
    const firstLoadRef = useRef(onFirstLoad)
    const errorRef = useRef(onError)

    firstLoadRef.current = onFirstLoad
    errorRef.current = onError

    /*
     * Tham số gửi lên `/config/standard-time/skus`, MỘT tín hiệu duy nhất về trạng thái bật/tắt.
     *
     * `getSkus` dựng `clean({active: true, ...params})` nên `active: true` luôn đứng trước; truyền
     * `{includeInactive: true}` chỉ THÊM một cờ thứ hai chứ không gỡ được cờ đầu, và query đi ra là
     * `active=true&includeInactive=true` — hai tham số nói ngược nhau, kết quả phụ thuộc hoàn toàn
     * vào việc service ưu tiên cái nào. `includeInactive` cũng không tồn tại ở bất kỳ đâu khác
     * trong repo (`git log --all -S includeInactive`), còn `active` thì có tài liệu.
     *
     * Cách repo-native để BỎ lọc: cho `active` thành `undefined` — `clean` loại nó khỏi query.
     *
     * ⚠️ Vẫn cần backend xác nhận: khi query KHÔNG có `active`, service trả cả SKU tắt hay vẫn mặc
     *    định lọc? Nếu nó vẫn lọc thì nhánh "tra cứu SKU đang Tắt" của `LookupPage` là code chết —
     *    nhưng ít nhất giờ ta gửi một tín hiệu rõ ràng thay vì hai tín hiệu chọi nhau.
     */
    const skuQuery = useCallback((extra = {}) => (
        includeInactive ? {...extra, active: undefined} : extra
    ), [includeInactive])

    const merge = useCallback((list) => {
        setSkus((prev) => {
            const byId = new Map(prev.map((item) => [item.skuId, item]))

            ;(list || []).forEach((item) => byId.set(item.skuId, item))

            return [...byId.values()].sort((left, right) => left.skuId.localeCompare(right.skuId))
        })
    }, [])

    useEffect(() => {
        alive.current = true

        getSkus(skuQuery())
            .then((list) => {
                if (!alive.current) return

                const sorted = [...(list || [])].sort((left, right) => left.skuId.localeCompare(right.skuId))

                merge(sorted)
                setSkuError(false)

                if (firstLoadRef.current) firstLoadRef.current(sorted)
            })
            .catch((e) => {
                if (!alive.current) return

                setSkuError(true)

                // Màn nào muốn tự báo lỗi theo kiểu của nó thì truyền `onError` — hook không áp đặt
                // một kiểu hiển thị chung cho mọi màn.
                if (errorRef.current) errorRef.current(e)
            })

        return () => {
            alive.current = false

            if (timer.current) clearTimeout(timer.current)
        }
    }, [merge, skuQuery])

    const searchSkus = useCallback((raw) => {
        const q = `${raw || ''}`.trim()

        if (timer.current) clearTimeout(timer.current)

        /*
         * Giành lượt NGAY tại đây, không đợi `setTimeout` nổ.
         *
         * `clearTimeout` phía trên chỉ huỷ được lượt CÒN TRONG DEBOUNCE. Timer nổ rồi thì nó là
         * no-op, request đang bay, và `.catch`/`.finally` của lượt đó vẫn chạy. Chốt `seq` là thứ
         * duy nhất tước quyền được của chúng — nên phải giành ở đây, cho CẢ hai nhánh dưới:
         *
         * - Giành muộn (bên trong `setTimeout`) thì suốt 300ms debounce lượt cũ vẫn thoả
         *   `mine === seq.current`. Gõ `A` → gõ `AB` → req của `A` hỏng → req của `AB` thành công:
         *   `skuSearchFailed` bật lên ở bước 3 và KHÔNG có ai hạ nó xuống, vì chỗ reset duy nhất
         *   nằm ngay dưới đây và đã chạy từ lúc gõ `AB`. Kết cục là "Tìm hỏng — thử lại" đứng trên
         *   một lượt tìm vừa thành công.
         * - Ô rỗng cũng phải giành, nếu không `.catch` của lượt người dùng vừa bỏ sẽ bật
         *   "Tìm hỏng — thử lại" lên một ô ĐANG RỖNG.
         *
         * antd bắn `onSearch('')` thật khi backspace về rỗng, bấm nút clear, và khi bấm chuột
         * xuống chính ô chọn — cái sau đo được ở cả `mode` đơn lẫn `multiple` trên antd 4.5.4, và
         * là cú click rất tự nhiên khi người dùng sốt ruột chờ mạng. (Còn "đóng dropdown khi đang
         * có chữ" thì chỉ bắn ở `mode` đơn, tức `LookupPage`, không bắn ở `GroupCard`.)
         */
        const mine = seq.current + 1

        seq.current = mine

        if (!q) {
            setSkuSearching(false)
            setSkuSearchFailed(false)

            return
        }

        /*
         * Bật cờ NGAY, không đợi `setTimeout` nổ.
         *
         * Đặt trong timeout thì suốt 300ms debounce `skuSearching` vẫn `false`, nên
         * `notFoundContent` rơi về "Không có SKU khớp" — đúng câu chữ mà tính năng này sinh ra để
         * diệt, và nói sai với đúng nhóm SKU nằm ngoài trang đầu (nhóm duy nhất không có match
         * tại chỗ).
         */
        setSkuSearching(true)
        setSkuSearchFailed(false)

        timer.current = setTimeout(() => {
            getSkus(skuQuery({q}))
                .then((list) => {
                    if (!alive.current) return

                    /*
                     * KHÔNG gác `seq` ở đây — chỉ CỜ mới cần chốt lượt, DỮ LIỆU thì không.
                     *
                     * `merge` hợp nhất theo `skuId` bằng `Map` rồi sort, nên nó KHÔNG BAO GIỜ
                     * làm mất mục nào — đúng hợp đồng "GỘP chứ không thay", và đó là lý do cho
                     * lượt đã bị bỏ đi qua vẫn an toàn.
                     *
                     * ⚠️ Nói cho chính xác: `merge` idempotent nhưng KHÔNG giao hoán. `byId.set()`
                     *    là kẻ ghi sau thắng, nên hai response cùng chứa một `skuId` cho kết quả
                     *    khác nhau tuỳ thứ tự về — một bản ghi cũ lọt về muộn có thể đè nhãn
                     *    `active`/`routingName` mới. Đây là đánh đổi có chủ ý: lựa chọn còn lại là
                     *    vứt dữ liệu, mà vứt dữ liệu chính là hồi quy đã đo được ở vòng 9. Muốn
                     *    đóng nốt thì hoãn `merge` của lượt cũ tới khi dropdown đóng, đừng gác
                     *    `seq` lại. Gác nó lại thì bấm vào ô trong
                     * lúc request đang bay sẽ VỨT đúng những SKU server vừa trả — tái lập nguyên
                     * con bug "gõ đúng mã vẫn không ra" mà cả tính năng này sinh ra để diệt.
                     *
                     * `setSkuError(false)` cũng vậy: một response 2xx là bằng chứng danh mục đọc
                     * được, bất kể lượt đó còn được quan tâm hay không. Gác nó thì banner "Không
                     * tải được danh mục SKU" của StageConfigPage treo lại dù danh mục vừa tự
                     * chứng minh là đọc được.
                     */
                    merge(list)
                    setSkuError(false)
                })
                .catch(() => {
                    // Giữ nguyên tập đang có, nhưng PHẢI phân biệt được với "thật sự không có mã":
                    // hook này phục vụ cả ô `mode="multiple"` của GroupCard lẫn ô đơn của
                    // LookupPage, và ở cả hai thì mã gõ tay không khớp option đều bị nuốt im lặng —
                    // người dùng không có đường vòng nào ngoài "Dán hàng loạt".
                    if (alive.current && mine === seq.current) setSkuSearchFailed(true)
                })
                .finally(() => {
                    if (alive.current && mine === seq.current) setSkuSearching(false)
                })
        }, SEARCH_DELAY_MS)
    }, [merge, skuQuery])

    // Tên cờ giữ nguyên tiền tố `sku` suốt chuỗi hook → màn → GroupCard, đừng đổi tên ở call-site.
    return {skus, skuError, skuSearching, skuSearchFailed, searchSkus}
}
